// Tiny per-page saved-views helper. A "view" is a named bag of key→string
// values (typically the current input values for a filter form). Stored in
// localStorage keyed by `apx-admin:views:<pageKey>`.
//
// Usage:
//   const ctl = mountSavedViews(container, 'rounds-explorer', {
//     capture: () => ({ gameId: gameIdEl.value, state: stateEl.value }),
//     apply: (values) => { gameIdEl.value = values.gameId ?? ''; ... refresh(); },
//   });

type Values = Record<string, string>;

interface SavedView {
  name: string;
  values: Values;
}

function storageKey(pageKey: string): string {
  return `apx-admin:views:${pageKey}`;
}

function load(pageKey: string): SavedView[] {
  try {
    const raw = localStorage.getItem(storageKey(pageKey));
    if (!raw) return [];
    const parsed = JSON.parse(raw) as SavedView[];
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
}

function save(pageKey: string, views: SavedView[]): void {
  localStorage.setItem(storageKey(pageKey), JSON.stringify(views));
}

export interface SavedViewsOpts {
  capture: () => Values;
  apply: (values: Values) => void | Promise<void>;
}

export function mountSavedViews(
  container: HTMLElement,
  pageKey: string,
  opts: SavedViewsOpts,
): void {
  function rerender(): void {
    const views = load(pageKey);
    container.innerHTML = `
      <select id="sv-pick" style="background:var(--panel-2);color:var(--text);border:1px solid var(--border);border-radius:var(--radius);padding:5px 8px;font-size:12px">
        <option value="">Saved views…</option>
        ${views.map((v) => `<option value="${escapeAttr(v.name)}">${escapeHtml(v.name)}</option>`).join('')}
      </select>
      <button id="sv-save" class="ghost" style="font-size:12px;padding:4px 10px">Save view</button>
      <button id="sv-delete" class="ghost" style="font-size:12px;padding:4px 10px" ${views.length === 0 ? 'disabled' : ''}>Delete</button>
    `;
    container.querySelector<HTMLSelectElement>('#sv-pick')!.addEventListener('change', (ev) => {
      const name = (ev.target as HTMLSelectElement).value;
      if (!name) return;
      const v = views.find((x) => x.name === name);
      if (v) void opts.apply(v.values);
    });
    container.querySelector<HTMLButtonElement>('#sv-save')!.addEventListener('click', () => {
      const name = prompt('Name this view:');
      if (!name) return;
      const trimmed = name.trim();
      if (!trimmed) return;
      const next = load(pageKey).filter((v) => v.name !== trimmed);
      next.push({ name: trimmed, values: opts.capture() });
      save(pageKey, next);
      rerender();
    });
    container.querySelector<HTMLButtonElement>('#sv-delete')!.addEventListener('click', () => {
      const pick = container.querySelector<HTMLSelectElement>('#sv-pick')!;
      const name = pick.value;
      if (!name) return;
      if (!confirm(`Delete saved view "${name}"?`)) return;
      save(pageKey, load(pageKey).filter((v) => v.name !== name));
      rerender();
    });
  }
  rerender();
}

function escapeHtml(s: string): string {
  return s.replace(/[&<>"']/g, (c) => {
    switch (c) {
      case '&': return '&amp;';
      case '<': return '&lt;';
      case '>': return '&gt;';
      case '"': return '&quot;';
      case "'": return '&#39;';
      default: return c;
    }
  });
}

function escapeAttr(s: string): string {
  return escapeHtml(s);
}
